/**
 * Voice tab audio graph. Headless — kit Voice tab is the start.
 *
 * Mic → AnalyserNode (listening RMS) → Flux STT (hottie-listen) →
 * useHottieChat.send (one brain, tools + transcript) → speakReply
 * (hottie-speak + existing playback tap).
 *
 * Publishes hottr:hottie:voice. No Orb CSS. No second Orb. Fail soft.
 */

import { getSupabase } from '@/lib/supabase';
import { sendThroughHottieChat } from './hottieChatSend';
import { listenUtterance } from './listen';
import { concatFloat32, floatToPcm16 } from './pcm';
import {
  DEFAULT_SPEED,
  DEFAULT_VOICE,
  speakReply,
  stopSpeak,
  waitForSpeakEnd,
} from './speak';
import {
  publishHottieVoice,
  resetHottieAudioContext,
  startAnalyserPump,
  unlockHottieAudio,
} from './voiceFeed';

const FFT_SIZE = 1024;
/** Quieter than the old 0.04 so a soft mic still closes an utterance. */
export const SPEECH_RMS = 0.015;
export const HOTTIE_VOICE_TEST_PARAM = 'hottieVoiceTest';
export const HOTTIE_VOICE_TEST_TRANSCRIPT = 'hey';
export const HOTTIE_VOICE_TEST_SPEAK = '__hottieVoiceTestSpeak__';

type HottieVoiceTestWindow = Window & {
  [HOTTIE_VOICE_TEST_SPEAK]?: () => void;
};

/** Netlify deploy-preview hosts only. Production `hottr.app` never matches. */
export function isHottieVoiceTestPreviewHost(hostname: string): boolean {
  return hostname.toLowerCase().includes('deploy-preview');
}

/**
 * Preview hook. Requires the `hottieVoiceTest` query AND a deploy-preview host.
 * `https://hottr.app/feed?hottieVoiceTest=1` stays dead.
 */
export function isHottieVoiceTestFlag(search?: string, hostname?: string): boolean {
  const raw =
    search ??
    (typeof window !== 'undefined' ? window.location?.search ?? '' : '');
  const host =
    hostname ??
    (typeof window !== 'undefined' ? window.location?.hostname ?? '' : '');
  if (!isHottieVoiceTestPreviewHost(host)) return false;
  try {
    return new URLSearchParams(raw).has(HOTTIE_VOICE_TEST_PARAM);
  } catch {
    return false;
  }
}

/**
 * Tight first try. `channelCount: 1` can throw OverconstrainedError /
 * NotFoundError on a real device. NotAllowedError is not a retry case.
 */
export const HOTTIE_VOICE_MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
  video: false,
};

/** Second try — no channelCount — so a present device is not rejected. */
export const HOTTIE_VOICE_MIC_FALLBACK: MediaStreamConstraints = { audio: true };

function gumErrorCode(err: unknown): string {
  if (!err || typeof err !== 'object') return '';
  const rec = err as { name?: unknown; message?: unknown };
  const name = typeof rec.name === 'string' ? rec.name : '';
  const message = typeof rec.message === 'string' ? rec.message : '';
  if (name && name !== 'Error' && name !== 'DOMException') return name;
  return message || name;
}

/** NotFoundError / OverconstrainedError only. NotAllowedError does not retry. */
export function isRetryableMicConstraintError(err: unknown): boolean {
  const code = gumErrorCode(err);
  return code === 'NotFoundError' || code === 'OverconstrainedError';
}

const SILENCE_MS = 700;
const MIN_SPEECH_MS = 280;
const MAX_UTTERANCE_MS = 12000;

let live = false;
let micStream: MediaStream | null = null;
let micAnalyser: AnalyserNode | null = null;
let processor: ScriptProcessorNode | null = null;
let stopPump: (() => void) | null = null;
let capture = false;
let chunks: Float32Array[] = [];
let speechStartedAt = 0;
let lastLoudAt = 0;
let vadTimer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;
let generation = 0;
let starting = false;

export function voiceGraphShouldSkipRestart(state: {
  live: boolean;
  hasMic: boolean;
  starting: boolean;
}): boolean {
  return state.live && (state.hasMic || state.starting);
}

function currentGen(): number {
  return generation;
}

function stopPumpNow(): void {
  stopPump?.();
  stopPump = null;
}

async function accessToken(): Promise<string | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

function downsampleCapture(audioCtx: AudioContext): ArrayBuffer | null {
  const joined = concatFloat32(chunks);
  chunks = [];
  if (!joined.length) return null;
  const pcm = floatToPcm16(joined, audioCtx.sampleRate, 16000);
  if (!pcm.byteLength) return null;
  const copy = new ArrayBuffer(pcm.byteLength);
  new Uint8Array(copy).set(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength));
  return copy;
}

function resumeAfterTurn(gen: number): void {
  inFlight = false;
  if (!live || gen !== currentGen()) return;
  startListeningPump();
}

async function onUtterance(audioCtx: AudioContext, gen: number): Promise<void> {
  if (!live || gen !== currentGen() || inFlight) return;
  const pcm = downsampleCapture(audioCtx);
  if (!pcm) {
    resumeAfterTurn(gen);
    return;
  }

  inFlight = true;
  stopPumpNow();
  publishHottieVoice('thinking', 0);

  try {
    const token = await accessToken();
    if (!token || !live || gen !== currentGen()) return;

    const heard = await listenUtterance(pcm, token);
    if (!live || gen !== currentGen()) return;
    if (!heard.ok) {
      console.warn('[hottie-voice]', heard.error);
      return;
    }

    await completeHeardTurn(heard.transcript, token, gen);
  } catch (err) {
    console.warn('[hottie-voice]', err);
  } finally {
    if (live && gen === currentGen()) resumeAfterTurn(gen);
  }
}

async function completeHeardTurn(
  transcript: string,
  token: string,
  gen: number,
): Promise<void> {
  const reply = await sendThroughHottieChat(transcript);
  if (!live || gen !== currentGen()) return;
  if (!reply) {
    console.warn('[hottie-voice]', "couldn't answer that");
    return;
  }

  const spoken = await speakReply({
    text: reply,
    accessToken: token,
    model: DEFAULT_VOICE,
    speed: DEFAULT_SPEED,
  });
  if (!spoken.ok) {
    console.warn('[hottie-voice]', spoken.error);
    return;
  }
  await waitForSpeakEnd();
}

/**
 * Same mouth as a real turn — chat then speak. Skips listen/VAD.
 * Preview only (`?hottieVoiceTest`).
 */
export async function injectHottieVoiceTestTurn(gen = currentGen()): Promise<void> {
  if (!live || gen !== currentGen() || inFlight) return;
  inFlight = true;
  stopPumpNow();
  publishHottieVoice('thinking', 0);
  try {
    const token = await accessToken();
    if (!token || !live || gen !== currentGen()) return;
    await completeHeardTurn(HOTTIE_VOICE_TEST_TRANSCRIPT, token, gen);
  } catch (err) {
    console.warn('[hottie-voice]', err);
  } finally {
    if (live && gen === currentGen()) resumeAfterTurn(gen);
  }
}

function testSpeakWindow(): HottieVoiceTestWindow | null {
  return typeof window === 'undefined' ? null : (window as HottieVoiceTestWindow);
}

function installHottieVoiceTestSpeakHook(): void {
  const win = testSpeakWindow();
  if (!win || !isHottieVoiceTestFlag()) return;
  win[HOTTIE_VOICE_TEST_SPEAK] = () => {
    void injectHottieVoiceTestTurn();
  };
}

function removeHottieVoiceTestSpeakHook(): void {
  const win = testSpeakWindow();
  if (!win) return;
  delete win[HOTTIE_VOICE_TEST_SPEAK];
}

/**
 * Preview inject only. Production stays on `listening` at level 0 so Brand's
 * Orb remains interested. Never snap back to idle. No fake transcript.
 */
function onMicUnavailable(gen: number): void {
  if (!live || gen !== currentGen()) return;
  if (isHottieVoiceTestFlag()) {
    void injectHottieVoiceTestTurn(gen);
    return;
  }
}

function startVad(audioCtx: AudioContext, analyser: AnalyserNode, gen: number): void {
  const buffer = new Float32Array(analyser.fftSize);
  stopVad();
  vadTimer = setInterval(() => {
    if (!live || inFlight || gen !== currentGen()) return;
    analyser.getFloatTimeDomainData(buffer as Float32Array<ArrayBuffer>);
    let sum = 0;
    for (let i = 0; i < buffer.length; i += 1) {
      const sample = buffer[i] ?? 0;
      sum += sample * sample;
    }
    const rms = buffer.length ? Math.sqrt(sum / buffer.length) : 0;
    const now = Date.now();
    if (rms >= SPEECH_RMS) {
      if (!capture) {
        capture = true;
        speechStartedAt = now;
        chunks = [];
      }
      lastLoudAt = now;
    }
    if (!capture) return;
    const spokenLongEnough = now - speechStartedAt >= MIN_SPEECH_MS;
    const silent = now - lastLoudAt >= SILENCE_MS;
    const tooLong = now - speechStartedAt >= MAX_UTTERANCE_MS;
    if ((spokenLongEnough && silent) || tooLong) {
      capture = false;
      void onUtterance(audioCtx, gen);
    }
  }, 80);
}

function stopVad(): void {
  if (vadTimer) clearInterval(vadTimer);
  vadTimer = null;
  capture = false;
  chunks = [];
}

function startListeningPump(): void {
  if (!live || !micAnalyser) return;
  stopPumpNow();
  stopPump = startAnalyserPump(micAnalyser, 'listening');
}

function detachMic(): void {
  stopVad();
  processor?.disconnect();
  processor = null;
  micAnalyser?.disconnect();
  micAnalyser = null;
  micStream?.getTracks().forEach((track) => track.stop());
  micStream = null;
}

async function requestMic(constraints: MediaStreamConstraints): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia(constraints);
}

async function attachMic(audioCtx: AudioContext, gen: number): Promise<void> {
  detachMic();
  try {
    try {
      micStream = await requestMic(HOTTIE_VOICE_MIC_CONSTRAINTS);
    } catch (err) {
      if (!isRetryableMicConstraintError(err)) throw err;
      micStream = await requestMic(HOTTIE_VOICE_MIC_FALLBACK);
    }
  } catch (err) {
    console.warn('[hottie-voice]', err);
    onMicUnavailable(gen);
    return;
  }
  const tracks = typeof micStream.getTracks === 'function' ? micStream.getTracks() : [];
  if (!tracks.length) {
    micStream = null;
    onMicUnavailable(gen);
    return;
  }
  if (!live || gen !== currentGen()) {
    tracks.forEach((track) => track.stop());
    micStream = null;
    return;
  }

  const source = audioCtx.createMediaStreamSource(micStream);
  micAnalyser = audioCtx.createAnalyser();
  micAnalyser.fftSize = FFT_SIZE;
  source.connect(micAnalyser);

  processor = audioCtx.createScriptProcessor(4096, 1, 1);
  processor.onaudioprocess = (event) => {
    if (!capture || !live) return;
    const input = event.inputBuffer.getChannelData(0);
    chunks.push(new Float32Array(input));
  };
  const mute = audioCtx.createGain();
  mute.gain.value = 0;
  source.connect(processor);
  processor.connect(mute);
  mute.connect(audioCtx.destination);

  startListeningPump();
  startVad(audioCtx, micAnalyser, gen);
}

/** Voice tab open. Start mic + analyser feed. Fail soft on mic deny. */
export function announceVoiceGraphListening(
  target?: EventTarget | null,
): void {
  publishHottieVoice('listening', 0, target);
}

export async function startHottieVoiceGraph(): Promise<void> {
  announceVoiceGraphListening();

  if (voiceGraphShouldSkipRestart({ live, hasMic: Boolean(micStream), starting })) {
    unlockHottieAudio();
    installHottieVoiceTestSpeakHook();
    return;
  }

  live = true;
  starting = true;
  generation += 1;
  const gen = generation;

  const audioCtx = unlockHottieAudio();
  installHottieVoiceTestSpeakHook();

  if (!audioCtx) {
    starting = false;
    return;
  }

  try {
    await attachMic(audioCtx, gen);
  } finally {
    if (gen === currentGen()) starting = false;
  }
}

/** Left Voice or sheet closed. Stop tracks, speak, analyser, and the feed. */
export function stopHottieVoiceGraph(): void {
  generation += 1;
  live = false;
  starting = false;
  inFlight = false;
  stopPumpNow();
  stopVad();
  detachMic();
  stopSpeak();
  resetHottieAudioContext();
  removeHottieVoiceTestSpeakHook();
}
